import * as THREE from 'three';
import { trackLoad } from '@/core/loadTracker';
import { tuning } from '@/config/tuning';
import { clamp, damp } from '@/math/angles';
import type { CarRenderSnapshot } from '@/entities/Car';
import { modelLoader, ModelLoader } from '@/render/ModelLoader';
import { VEHICLES, type VehicleDef } from '@/entities/vehicles';
import {
  carbonWeaveTexture,
  carPartOf,
  ensureFlakeUVs,
  liveryTexture,
  trimIsDark,
  measureLivery,
  paintFlakeTexture,
  projectBoxUVs,
  projectLiveryViews,
  projectWrapUVs,
  stockPaintFor,
  type CarLook,
} from '@/entities/carLook';
import { patchLiveryMaterial, patchWrapDecal } from '@/entities/livery/atlas';
import { clearArtWheelFronts, fitArtWheels, isWheelLid, projectWheelFaces, wheelArt, wheelCorner } from '@/entities/livery/wheels';
import { glowRingTexture } from '@/world/scenery/propKit';
import { buildModRig, type ModRig } from '@/entities/mods/ModRig';
import { surfaceFromObject, type SurfaceField } from '@/entities/mods/modSurface';
import { vehicleBoxFor } from '@/config/tuning';
import { BoostFx } from '@/entities/fx/BoostFx';
import { MagnetField } from '@/entities/fx/MagnetField';
import { ShieldBubble } from '@/entities/fx/ShieldBubble';

/**
 * CarView.ts — the visual car (M12 redesign): a proper low-poly SPORTS CAR —
 * wedge hull extruded from a side profile, glass canopy, spoiler, splitter,
 * emissive head/tail lights, tyres with bright rims — plus the "fake suspension"
 * that sells weight cheaply (bank into turns, pitch under accel/brake), wheels
 * that spin with speed and steer at the front, and a colour aura for racer
 * identity.
 *
 * Two more M12 looks live here:
 *  - the BULLDOZER transformation: while the dozer item runs, the sports body
 *    swaps for a chunky dozer (blade, cab, stack, track skirts) with a quick
 *    scale-pop crossfade;
 *  - the SHIELD bubble: an additive sphere that fades up while shielded.
 *
 * All of this is cosmetic — it reads the Car's interpolated snapshot and never
 * feeds back into the physics (the physics box in collisions.ts matches this
 * footprint: ~4.0 long × ~2.2 wide).
 */

export const BODY_WIDTH = 2.05;
export const GLASS_WIDTH = 1.62;

/** The sports-car side profile (x = along the car, +front; y = up). */
export function bodyProfile(): THREE.Shape {
  const s = new THREE.Shape();
  s.moveTo(2.0, 0.18); // front bumper lip
  s.lineTo(2.02, 0.5); // low wedge nose
  s.lineTo(1.05, 0.66); // hood rise
  s.lineTo(-1.25, 0.8); // rear deck
  s.lineTo(-1.95, 0.92); // ducktail kick
  s.lineTo(-2.0, 0.6); // tail face
  s.lineTo(-2.0, 0.18); // rear bumper bottom
  s.lineTo(2.0, 0.18);
  return s;
}

/** The glass canopy profile (sits on the beltline). */
export function canopyProfile(): THREE.Shape {
  const s = new THREE.Shape();
  s.moveTo(0.95, 0.62); // windshield base
  s.lineTo(0.18, 1.14); // raked windshield → roof
  s.lineTo(-0.62, 1.12); // short roof
  s.lineTo(-1.3, 0.76); // fastback rear glass
  s.lineTo(0.95, 0.62);
  return s;
}

/** Extrude a profile across the car's width, centred. */
export function extrudeProfile(shape: THREE.Shape, width: number): THREE.ExtrudeGeometry {
  const geo = new THREE.ExtrudeGeometry(shape, { depth: width, bevelEnabled: false });
  geo.rotateY(-Math.PI / 2); // profile plane → world ZY; extrusion → world X
  geo.translate(width / 2, 0, 0); // centre the width
  return geo;
}

export class CarView {
  /** Outer node: world position + heading (this is what moves through the world). */
  readonly group = new THREE.Group();
  /** Inner node: the leaning body (roll + pitch), so wheels can stay grounded. */
  private readonly body = new THREE.Group();
  /** The two swappable forms inside `body`. */
  private readonly sportGroup = new THREE.Group();
  private readonly dozerGroup = new THREE.Group();
  private readonly wheels: THREE.Mesh[] = [];
  private readonly steerPivots: THREE.Group[] = [];
  /** M79c: spin pivots for a loaded GLB's own wheels (Tyre_/Rim_/barrel by
   *  id suffix), each rotated about its axle so the real model wheels roll —
   *  the procedural `wheels` above are hidden once a GLB lands, so without
   *  this NO model's wheels turned (glaring on the monster truck's big tyres). */
  private readonly wheelPivots: { pivot: THREE.Group; axis: THREE.Vector3 }[] = [];
  /** Procedural body parts (everything in sportGroup except the boost flame) —
   *  hidden once a GLB vehicle model loads in (M35 Stage 2). */
  private readonly procMeshes: THREE.Object3D[] = [];

  private smoothY = 0;
  private placed = false;
  private roll = 0;
  private pitch = 0;
  private prevSpeed = 0;
  private wheelSpin = 0;

  // Colour-aura flare while boosting (power-up cue; also visible on rivals).
  private aura!: THREE.Mesh;
  private auraMat!: THREE.MeshBasicMaterial;
  // Additive glow needs more alpha than the old solid paint ring did (GS4).
  private readonly auraBaseOpacity = 0.42;
  private boostFlare = 0;
  private boostTarget = 0;
  /** M145: afterburners, light trails, wind streaks, ignition ring. */
  private readonly boostFx = new BoostFx();
  private tailMat!: THREE.MeshStandardMaterial;

  // Bulldozer transformation + shield bubble (M12; the bubble rebuilt M145).
  private dozerBlend = 0;
  private dozerTarget = 0;
  private readonly shield = new ShieldBubble();
  // MAGNET (M145): field lines + pulse arches from the car onto its ball.
  private readonly magnetField = new MagnetField(tuning.ball.radius);
  private magnetFade = 0;
  private magnetTarget = 0;
  /** The held ball's centre in WORLD space (fed each frame while it runs). */
  private readonly magnetBall = new THREE.Vector3();
  // KICK (M145): an impact burst where the ball was struck.
  private kickBurst!: THREE.Sprite;
  private kickBurstMat!: THREE.SpriteMaterial;
  private kickBurstTimer = 0;
  // OFFSIDE TRAP (M145): the linesman's flag snaps up over the car that fired.
  private offsideFlag!: THREE.Sprite;
  private offsideFlagMat!: THREE.SpriteMaterial;
  private offsideFlagTimer = 0;
  // Pibe de Oro (M46, rebuilt M58b — owner: NO bubble): the star power is
  // worn entirely as the Maradona mop, and the golden glow lives IN the hair
  // itself — a hot emissive on the curls plus a per-curl additive rim shell.
  private pibeFade = 0;
  private pibeTarget = 0;
  private pibeHair!: THREE.Group;
  private readonly pibeLocks: { mesh: THREE.Mesh; base: THREE.Vector3 }[] = [];
  /** M58c: the locks DRAPING down each flank of the car (they sway, they
   *  don't stream — separate list so update() animates them differently). */
  private readonly pibeFalls: { mesh: THREE.Mesh; base: THREE.Vector3 }[] = [];
  private pibeHairMat!: THREE.MeshStandardMaterial;
  private pibeHairGlowMat!: THREE.MeshBasicMaterial;
  // Spin-out FX (M58): dizzy stars orbiting the cab + tyre-smoke puffs while
  // the car is stunned (dozer ram, slide tackle, offside trap, pibe smash,
  // weight clash). Driven by the synced stunTimer, so it works online too.
  private stunGroup!: THREE.Group;
  private readonly stunStars: THREE.Sprite[] = [];
  private readonly stunPuffs: { sprite: THREE.Sprite; mat: THREE.SpriteMaterial; phase: number }[] =
    [];
  private stunFade = 0;
  private stunTarget = 0;
  // STUDS OUT (M59): big silver spinning spikes on the wheels while active.
  private studsGroup!: THREE.Group;
  private readonly studHubs: THREE.Group[] = [];
  private studsFade = 0;
  private studsTarget = 0;
  private readonly vehicleId: string;
  // SCORPION KICK (M59): the body backflips in place while the ball flies.
  // M115 (owner): the BULLDOZER victim's two-flip tumble. Self-clocked from the
  // 'dozerHit' SimEvent rather than a synced field — events already reach every
  // client, so this costs ZERO protocol change (the M58 stun-rig precedent).
  private tumbleT = 0;
  private tumbleDur = 0;
  /** Yaw the body must reach by touchdown so the car lands pointing DOWN-COURSE
   *  (owner: "ensure the car lands facing forwards on the track"). Supplied by
   *  the race layer, which is what knows where the track is pointing. */
  private tumbleYawFix = 0;
  private scorpionTimerLeft = 0; // raw seconds left on the car lock (M60b)
  private scorpionFlipStarted = false;
  // Anime "slam kick" impact burst (M60) — a manga starburst that pops at the
  // moment of the kick and fades over ~half a second.
  private scorpionBurst!: THREE.Sprite;
  private scorpionBurstMat!: THREE.SpriteMaterial;
  private scorpionBurstTimer = 0;
  // SLIPPERY MUD deploy spray (M60b) — mud blobs that shoot out the back when
  // the puddle is poured. A one-shot ballistic burst.
  private readonly mudSpray: { sprite: THREE.Sprite; vx: number; vy: number; vz: number }[] = [];
  private mudSprayTimer = 0;
  private t = 0;
  /** M128: the equipped MODS (built once from the look; presentation only). */
  private modRig: ModRig | null = null;
  /** M128c: the fitted model's measured top surface (null until it lands). */
  private surface: SurfaceField | null = null;
  private dribbling = false;
  private accelSmooth = 0;

  constructor(
    color = 0xff3b3b,
    vehicle?: VehicleDef,
    /** M79 — the equipped cosmetic look (offline player car; display only). */
    private readonly look?: CarLook,
  ) {
    this.vehicleId = vehicle?.id ?? 'sports';
    this.group.add(this.body);
    this.body.add(this.sportGroup, this.dozerGroup);
    this.buildSportsBody(color);
    this.buildDozerBody(color);
    this.buildWheels();
    this.buildAura(color);
    this.group.add(this.shield.mesh, this.magnetField.group);
    this.buildItemPops();
    this.buildPibeHair();
    this.buildStunFx();
    this.buildStuds();
    this.buildScorpionBurst();
    this.buildMudSpray();
    this.dozerGroup.visible = false;
    // M128: the equipped MODS ride on `body`, so they lean with the fake
    // suspension exactly like the Pibe hair does.
    this.modRig = buildModRig(look, this.vehicleId, color, this.surface);
    if (this.modRig) this.body.add(this.modRig.group);

    // Everything in sportGroup EXCEPT the boost effects is the procedural body —
    // track it so a loaded GLB vehicle can hide it (keeping flame/aura/shield).
    for (const c of this.sportGroup.children) {
      if (c !== this.boostFx.group) this.procMeshes.push(c);
    }

    // M35 Stage 2: swap in the real 3D vehicle model (async). The procedural body
    // above stays as an instant placeholder until the GLB lands, then hides.
    if (vehicle) this.loadModel(vehicle, color);
  }

  /** Load the racer's GLB vehicle, fit it to the physics box, recolour its paint
   *  per racer, and hide the procedural placeholder body + wheels. On failure the
   *  procedural car simply remains. */
  private loadModel(vehicle: VehicleDef, color: number): void {
    trackLoad(modelLoader.load(vehicle.url))
      .then((g) => {
        const fitted = ModelLoader.fit(g, vehicle.length, vehicle.forwardIsZ);
        applyCarMaterials(fitted, color, this.look, vehicle.id);
        for (const m of this.procMeshes) m.visible = false;
        for (const w of this.wheels) w.visible = false;
        this.sportGroup.add(fitted);
        this.spinifyWheels(fitted);
        // M128c: the mods learn the roof, glass and deck this model REALLY
        // has (the cape drapes on it, the hair floors on it, the rally pod
        // stands on it) — the anchor table only ever approximated them.
        this.surface = surfaceFromObject(fitted, this.body);
        this.modRig?.setSurface(this.surface);
      })
      .catch((e) => {
        console.warn('[CarView] vehicle model failed to load; keeping procedural body', e);
      });
  }

  /**
   * Wrap each of a loaded GLB's wheels (Tyre_/Rim_/Trim_barrel_ grouped by the
   * shared id suffix — FL/FR/RL/RR etc.) in a pivot at its hub so the real
   * model wheels can roll. The axle is the car's left-right axis (all wheels
   * share it); brake calipers and the chassis are left static by not matching
   * them. Called once per model load; the clone is per-car so this is safe.
   */
  private spinifyWheels(fitted: THREE.Object3D): void {
    this.group.updateWorldMatrix(true, true);
    // The roll axis every wheel turns about, in world space.
    const axleWorld = new THREE.Vector3(1, 0, 0)
      .transformDirection(this.group.matrixWorld)
      .normalize();

    // Collect wheel meshes by id suffix (the tyre carries the hub centre).
    const groups = new Map<string, { meshes: THREE.Mesh[]; tyre: THREE.Mesh | null }>();
    fitted.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      const match = /^(Tyre|Rim|Trim_barrel)_(.+)$/.exec(m.name);
      if (!match) return;
      const key = wheelCorner(match[2]);
      let e = groups.get(key);
      if (!e) groups.set(key, (e = { meshes: [], tyre: null }));
      e.meshes.push(m);
      if (match[1] === 'Tyre') e.tyre = m;
    });

    const wbox = new THREE.Box3();
    const wc = new THREE.Vector3();
    const invParent = new THREE.Matrix4();
    for (const { meshes, tyre } of groups.values()) {
      const anchor = tyre ?? meshes[0];
      const parent = anchor.parent;
      if (!parent) continue;
      wbox.setFromObject(anchor); // hub = tyre's world-space centre
      wbox.getCenter(wc);
      parent.updateWorldMatrix(true, false);
      invParent.copy(parent.matrixWorld).invert();
      const pivot = new THREE.Group();
      pivot.position.copy(wc).applyMatrix4(invParent); // world hub → parent-local
      parent.add(pivot);
      for (const m of meshes) pivot.attach(m); // reparent, keeping world transform
      const axis = axleWorld.clone().transformDirection(invParent).normalize();
      this.wheelPivots.push({ pivot, axis });
    }
  }

  private buildSportsBody(color: number): void {
    // M35: real two-layer CAR PAINT — a coloured metallic base under a glossy
    // clearcoat. The clearcoat is the wet, deep "showroom" lacquer that reflects
    // the IBL sky sharply; without an environment map (scene.environment) this
    // would just look dark, which is why M35 added the env map first.
    const paint = new THREE.MeshPhysicalMaterial({
      color,
      roughness: 0.32,
      metalness: 0.5,
      clearcoat: 1.0,
      clearcoatRoughness: 0.08,
      envMapIntensity: 1.3,
    });
    // Black chassis / trim — a semi-gloss dark dielectric that catches a rim of sky.
    const dark = new THREE.MeshStandardMaterial({
      color: 0x0b0c11,
      roughness: 0.45,
      metalness: 0.35,
      envMapIntensity: 1.0,
    });
    // Tinted, mirror-like glass canopy (reflective, cheap — no transmission cost).
    const glass = new THREE.MeshPhysicalMaterial({
      color: 0x0a0e18,
      roughness: 0.06,
      metalness: 0.1,
      clearcoat: 1.0,
      clearcoatRoughness: 0.04,
      envMapIntensity: 1.6,
    });

    // Hull + canopy (extruded side profiles — the crisp sports silhouette).
    const hull = new THREE.Mesh(extrudeProfile(bodyProfile(), BODY_WIDTH), paint);
    hull.castShadow = true;
    const canopy = new THREE.Mesh(extrudeProfile(canopyProfile(), GLASS_WIDTH), glass);
    canopy.castShadow = true;
    this.sportGroup.add(hull, canopy);

    // Front splitter + side skirts (low, dark trim that grounds the silhouette).
    const splitter = new THREE.Mesh(new THREE.BoxGeometry(2.14, 0.1, 0.55), dark);
    splitter.position.set(0, 0.14, 1.85);
    const skirtGeo = new THREE.BoxGeometry(0.1, 0.16, 2.7);
    const skirtL = new THREE.Mesh(skirtGeo, dark);
    skirtL.position.set(-1.02, 0.16, -0.1);
    const skirtR = new THREE.Mesh(skirtGeo, dark);
    skirtR.position.set(1.02, 0.16, -0.1);
    this.sportGroup.add(splitter, skirtL, skirtR);

    // Rear wing on two struts.
    const wing = new THREE.Mesh(new THREE.BoxGeometry(1.9, 0.07, 0.46), paint);
    wing.position.set(0, 1.06, -1.95);
    wing.castShadow = true;
    const strutGeo = new THREE.BoxGeometry(0.09, 0.28, 0.12);
    const strutL = new THREE.Mesh(strutGeo, dark);
    strutL.position.set(-0.6, 0.9, -1.9);
    const strutR = new THREE.Mesh(strutGeo, dark);
    strutR.position.set(0.6, 0.9, -1.9);
    this.sportGroup.add(wing, strutL, strutR);

    // Lights: white-hot headlight slits, one full-width tail bar (flares on boost).
    const headMat = new THREE.MeshStandardMaterial({
      color: 0xfff7df,
      emissive: 0xffe9b0,
      emissiveIntensity: 0.9,
    });
    const headGeo = new THREE.BoxGeometry(0.5, 0.1, 0.08);
    const headL = new THREE.Mesh(headGeo, headMat);
    headL.position.set(-0.62, 0.5, 1.98);
    headL.rotation.y = 0.18;
    const headR = new THREE.Mesh(headGeo, headMat);
    headR.position.set(0.62, 0.5, 1.98);
    headR.rotation.y = -0.18;
    this.tailMat = new THREE.MeshStandardMaterial({
      color: 0x3a0608,
      emissive: 0xff2230,
      emissiveIntensity: 0.8,
    });
    const tail = new THREE.Mesh(new THREE.BoxGeometry(1.75, 0.09, 0.06), this.tailMat);
    tail.position.set(0, 0.82, -2.0);
    this.sportGroup.add(headL, headR, tail);

    // Twin exhausts + the boost effects (M145: see fx/BoostFx.ts).
    const pipeGeo = new THREE.CylinderGeometry(0.07, 0.07, 0.16, 10);
    pipeGeo.rotateX(Math.PI / 2);
    const pipeL = new THREE.Mesh(pipeGeo, dark);
    pipeL.position.set(-0.4, 0.32, -2.02);
    const pipeR = new THREE.Mesh(pipeGeo, dark);
    pipeR.position.set(0.4, 0.32, -2.02);
    this.sportGroup.add(pipeL, pipeR, this.boostFx.group);
  }

  /** The bulldozer form (shown while the dozer item runs).
   *
   *  M115 (owner): "the bulldozer itself is better designed and also keeps the
   *  paint job of the users current vehicle."
   *
   *  KEEPS THE PAINT JOB: the old version painted the hull with a FLAT
   *  MeshStandardMaterial({color}), so a player wearing the Brazil wrap or a
   *  kit lost their livery the moment the item fired. The paint material now
   *  mirrors what applyCarMaterials does for the real body — the equipped
   *  wrap/kit MAP wins, and the map's presence forces the base colour white so
   *  the texture reads at its own colours (the M102 rule). Falls back to the
   *  racer colour when nothing is equipped.
   *
   *  BETTER DESIGNED: a proper V-blade with push arms and hydraulic rams, a
   *  ROPS roll cage over the cab, a stack with a tip, and crawler tracks with
   *  visible rollers + a drive sprocket — instead of the old flat slab, two
   *  sticks and a pair of plain skirts. */
  private buildDozerBody(color: number): void {
    // The equipped livery (wrap or kit) — the same map the real body wears.
    const wrap = this.look?.wrapMap;
    const paint = new THREE.MeshStandardMaterial({
      color: wrap ? 0xffffff : (this.look?.body ?? color),
      map: wrap ?? null,
      roughness: 0.5,
      metalness: 0.25,
    });
    const steel = new THREE.MeshStandardMaterial({
      color: 0x9aa2ad,
      roughness: 0.3,
      metalness: 0.78,
      envMapIntensity: 1.3,
    });
    const dark = new THREE.MeshStandardMaterial({ color: 0x1a1b20, roughness: 0.85 });
    const glass = new THREE.MeshStandardMaterial({
      color: 0x101622,
      roughness: 0.1,
      metalness: 0.6,
    });
    const hazard = new THREE.MeshStandardMaterial({
      color: 0xffb020,
      emissive: 0xff8a00,
      emissiveIntensity: 0.5,
      roughness: 0.6,
    });

    // ---- chassis + cab ----
    const hull = new THREE.Mesh(new THREE.BoxGeometry(1.9, 0.85, 2.7), paint);
    hull.position.set(0, 0.78, -0.25);
    hull.castShadow = true;

    const cab = new THREE.Mesh(new THREE.BoxGeometry(1.45, 0.85, 1.25), paint);
    cab.position.set(0, 1.55, -0.75);
    cab.castShadow = true;
    const cabGlass = new THREE.Mesh(new THREE.BoxGeometry(1.2, 0.5, 1.27), glass);
    cabGlass.position.set(0, 1.62, -0.75);

    // ROPS roll cage — four posts and a roof rail. Reads as real machinery and
    // gives the silhouette something to catch light on while it tumbles past.
    const parts: THREE.Object3D[] = [hull, cab, cabGlass];
    const postGeo = new THREE.BoxGeometry(0.09, 0.62, 0.09);
    for (const [px, pz] of [
      [-0.68, -0.18],
      [0.68, -0.18],
      [-0.68, -1.32],
      [0.68, -1.32],
    ] as const) {
      const post = new THREE.Mesh(postGeo, steel);
      post.position.set(px, 2.05, pz);
      parts.push(post);
    }
    const roof = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.1, 1.42), steel);
    roof.position.set(0, 2.38, -0.75);
    roof.castShadow = true;
    parts.push(roof);

    const stack = new THREE.Mesh(new THREE.CylinderGeometry(0.1, 0.13, 0.85, 10), dark);
    stack.position.set(0.5, 1.55, 0.25);
    const stackTip = new THREE.Mesh(new THREE.CylinderGeometry(0.15, 0.15, 0.12, 10), steel);
    stackTip.position.set(0.5, 2.02, 0.25);
    const beacon = new THREE.Mesh(
      new THREE.BoxGeometry(0.16, 0.14, 0.16),
      new THREE.MeshStandardMaterial({
        color: 0xff9f43,
        emissive: 0xff7a1a,
        emissiveIntensity: 1.4,
      }),
    );
    beacon.position.set(0, 2.52, -0.75);
    parts.push(stack, stackTip, beacon);

    // ---- the BLADE: a real V, not a flat slab ----
    // Two panels angled back from the centre, so it looks like it would throw a
    // car aside rather than stop it dead.
    const bladeGeo = new THREE.BoxGeometry(1.45, 1.0, 0.2);
    for (const side of [-1, 1] as const) {
      const half = new THREE.Mesh(bladeGeo, steel);
      half.position.set(side * 0.66, 0.66, 1.88);
      half.rotation.y = side * 0.2; // the V
      half.rotation.x = -0.14; // tipped back at the top
      half.castShadow = true;
      parts.push(half);
      // Hazard chevron along the top edge of each panel.
      const stripe = new THREE.Mesh(new THREE.BoxGeometry(1.45, 0.16, 0.06), hazard);
      stripe.position.set(side * 0.66, 1.08, 1.83);
      stripe.rotation.y = side * 0.2;
      parts.push(stripe);
      // Cutting edge along the bottom.
      const edge = new THREE.Mesh(new THREE.BoxGeometry(1.45, 0.14, 0.28), dark);
      edge.position.set(side * 0.66, 0.19, 1.95);
      edge.rotation.y = side * 0.2;
      parts.push(edge);
      // Push arm + hydraulic ram back to the chassis.
      const arm = new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.16, 1.5), dark);
      arm.position.set(side * 0.9, 0.5, 1.05);
      arm.rotation.x = -0.08;
      const ram = new THREE.Mesh(new THREE.CylinderGeometry(0.075, 0.075, 1.05, 8), steel);
      ram.position.set(side * 0.62, 1.0, 1.25);
      ram.rotation.x = Math.PI / 2 - 0.45;
      parts.push(arm, ram);
    }

    // ---- crawler tracks: skirt + rollers + drive sprocket ----
    for (const side of [-1, 1] as const) {
      const skirt = new THREE.Mesh(new THREE.BoxGeometry(0.58, 0.62, 3.1), dark);
      skirt.position.set(side * 0.85, 0.5, -0.1);
      skirt.castShadow = true;
      parts.push(skirt);
      const sprocket = new THREE.Mesh(new THREE.CylinderGeometry(0.34, 0.34, 0.62, 12), steel);
      sprocket.position.set(side * 0.85, 0.5, -1.45);
      sprocket.rotation.z = Math.PI / 2;
      parts.push(sprocket);
      for (let i = 0; i < 3; i++) {
        const roller = new THREE.Mesh(new THREE.CylinderGeometry(0.17, 0.17, 0.64, 8), steel);
        roller.position.set(side * 0.85, 0.26, -0.85 + i * 0.85);
        roller.rotation.z = Math.PI / 2;
        parts.push(roller);
      }
    }

    this.dozerGroup.add(...parts);
  }

  private buildWheels(): void {
    const tyreGeo = new THREE.CylinderGeometry(0.46, 0.46, 0.36, 16);
    tyreGeo.rotateZ(Math.PI / 2); // axle along local X so it rolls forward about X
    const rimGeo = new THREE.CylinderGeometry(0.27, 0.27, 0.38, 12);
    rimGeo.rotateZ(Math.PI / 2);
    // Matte rubber tyre + a polished alloy rim that gleams against the IBL.
    // M114: the procedural placeholder/fallback body now honours the equipped
    // tire set (colour + chrome + metal rubber) with the SAME values
    // applyCarMaterials uses for GLBs — before this the placeholder always
    // showed stock wheels, a visible pop when the model landed.
    const tyreMat = new THREE.MeshStandardMaterial(tyreFinish(this.look));
    // M151: an undressed ride wears its VehicleDef's stock rim finish.
    const stockRim = VEHICLES[this.vehicleId];
    const rimChrome = this.look?.rimChrome ?? (this.look?.rim === undefined && stockRim?.rimChrome === true);
    const rimMat = new THREE.MeshStandardMaterial({
      color: this.look?.rim ?? stockRim?.rimColor ?? 0xd2d8e2,
      roughness: rimChrome ? 0.06 : 0.18,
      metalness: rimChrome ? 1.0 : 0.92,
      envMapIntensity: rimChrome ? 1.8 : 1.4,
    });

    const corners: [number, number, number, boolean][] = [
      [0.92, 0.46, 1.32, true], // front-right (steers)
      [-0.92, 0.46, 1.32, true], // front-left (steers)
      [0.92, 0.46, -1.32, false], // rear-right
      [-0.92, 0.46, -1.32, false], // rear-left
    ];

    for (const [wx, wy, wz, isFront] of corners) {
      const wheel = new THREE.Mesh(tyreGeo, tyreMat);
      wheel.castShadow = true;
      const rim = new THREE.Mesh(rimGeo, rimMat);
      wheel.add(rim); // spins with the tyre
      this.wheels.push(wheel);
      if (isFront) {
        const pivot = new THREE.Group();
        pivot.position.set(wx, wy, wz);
        pivot.add(wheel);
        this.steerPivots.push(pivot);
        this.group.add(pivot);
      } else {
        wheel.position.set(wx, wy, wz);
        this.group.add(wheel);
      }
    }
  }

  /** GS4: the identity aura is a SOFT GLOW, not paint — a feathered radial
   *  ring texture (shared 64px canvas from propKit) on an additive plane. The
   *  old hard-edged constant-alpha RingGeometry read as red smears stencilled
   *  on the asphalt wherever the body occluded its middle. */
  private buildAura(color: number): void {
    this.auraMat = new THREE.MeshBasicMaterial({
      color,
      map: glowRingTexture(),
      transparent: true,
      opacity: this.auraBaseOpacity,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    // Plane (not a ring) so the texture feathers BOTH edges; ring peak ≈ 73%
    // of the half-size → 5.4-wide plane puts the glow band at r ≈ 1.97.
    this.aura = new THREE.Mesh(new THREE.PlaneGeometry(5.4, 5.4), this.auraMat);
    this.aura.rotation.x = -Math.PI / 2;
    this.aura.position.y = 0.05;
    this.aura.renderOrder = 1;
    this.group.add(this.aura);
  }

  /** M145: two one-shot sprites — the KICK's impact burst (it reuses the
   *  scorpion's manga starburst, tinted hot orange) and the OFFSIDE TRAP's
   *  linesman flag. Both hide themselves when their timer runs out. */
  private buildItemPops(): void {
    this.kickBurstMat = new THREE.SpriteMaterial({
      map: scorpionBurstTexture(),
      color: 0xffb070,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.kickBurst = new THREE.Sprite(this.kickBurstMat);
    this.kickBurst.visible = false;
    this.group.add(this.kickBurst);

    this.offsideFlagMat = new THREE.SpriteMaterial({
      map: offsideFlagTexture(),
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
    this.offsideFlag = new THREE.Sprite(this.offsideFlagMat);
    this.offsideFlag.center.set(0.18, 0.08); // pivots at the foot of the stick
    this.offsideFlag.visible = false;
    this.group.add(this.offsideFlag);
  }

  /** The hair of El Diez (M46; supersized M58; bubble-free M58b; reshaped
   *  M58c — owner: "lower height, don't block the view above the car, and
   *  it should fall down each side"): while PIBE DE ORO runs the car wears
   *  a LOW, WIDE flowing Maradona mop — a flattened crown of dark curls
   *  hugging the roofline (top ≈ y 3.1, was 4.6), locks streaming BEHIND
   *  the car and stretched FALLS draping down both flanks. NOTE the §6.1
   *  convention: in CarView local space the car FRONT is +Z (rotation.y =
   *  +heading fronts +Z), so the trailing locks go toward -Z and the falls
   *  sit at ±X. The GLOW is built INTO the hair, two layers per curl: a
   *  pulsing golden emissive on the curl surface (dark↔gold Mario-star
   *  flash) + a whisper-faint additive BackSide rim shell parented to each
   *  curl. No dome, no shield-style sphere. ~28 curls × 2 shells ≈ 2.7k
   *  tris. */
  private buildPibeHair(): void {
    this.pibeHair = new THREE.Group();
    // Unit sphere, scaled per curl (6×5 segments = 48 tris each — curls read
    // fine this chunky at racing distance, and ~25 stay under 1.3k tris).
    const curlGeo = new THREE.SphereGeometry(1, 6, 5);
    // Dark brown curls with a golden EMISSIVE rim — the hair itself glows
    // (the bloom pass picks the emissive up at night, like the neon).
    this.pibeHairMat = new THREE.MeshStandardMaterial({
      color: 0x1c1006,
      roughness: 0.8,
      metalness: 0.0,
      emissive: 0xe89a1e,
      emissiveIntensity: 0.5,
    });
    // The per-curl rim: BackSide + additive = a golden aura hugging each
    // curl's silhouette (never a bubble — it follows the hair's own shape).
    this.pibeHairGlowMat = new THREE.MeshBasicMaterial({
      color: 0xffb830,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      side: THREE.BackSide,
      depthWrite: false,
    });
    const hairMat = this.pibeHairMat;
    const glowMat = this.pibeHairGlowMat;
    const curl = (
      x: number,
      y: number,
      z: number,
      r: number,
      stretchZ = 1,
      stretchY = 1,
    ): THREE.Mesh => {
      const m = new THREE.Mesh(curlGeo, hairMat);
      m.position.set(x, y, z);
      // stretchZ > 1 = a trailing lock; stretchY > 1 = a falling side lock.
      m.scale.set(r, r * stretchY, r * stretchZ);
      // The rim shell rides as a child: it inherits the curl's position,
      // sway and stretch for free (locks flow, their glow flows with them).
      const rim = new THREE.Mesh(curlGeo, glowMat);
      rim.scale.setScalar(1.22);
      m.add(rim);
      this.pibeHair.add(m);
      return m;
    };

    // The mop (M58c: LOW + WIDE): a flattened crown over two broad rings
    // hugging the roofline — big and unmistakable, but topping out ≈ y 3.1
    // (was 4.6) so it stays under the chase cam's sightline to the road.
    curl(0, 2.55, -0.2, 0.62);
    const UPPER = 6;
    for (let i = 0; i < UPPER; i++) {
      const a = (i / UPPER) * Math.PI * 2;
      // Rings squashed slightly along Z so the mass reads mop, not planet.
      curl(Math.cos(a) * 0.85, 2.3, -0.2 + Math.sin(a) * 0.75, 0.56 + (i % 3) * 0.05);
    }
    const LOWER = 6;
    for (let i = 0; i < LOWER; i++) {
      const a = ((i + 0.5) / LOWER) * Math.PI * 2; // offset half a step: no seams
      curl(Math.cos(a) * 1.2, 1.8, -0.2 + Math.sin(a) * 1.0, 0.52 + (i % 2) * 0.07);
    }
    // A curly fringe dropping toward the windshield — the classic Diego forelock.
    curl(0, 1.95, 1.1, 0.5);
    curl(0.5, 2.1, 0.95, 0.42);
    curl(-0.5, 2.1, 0.95, 0.42);

    // SIDE FALLS (M58c, owner): stretched curls DRAPING down each flank —
    // the mop doesn't just sit on top, it falls around the sides of the car
    // (roofline down over the windows). update() gives them a gentle
    // pendulum sway on their own phases.
    const falls: [number, number, number, number][] = [
      [-1.3, 1.25, 0.35, 0.4],
      [-1.38, 1.1, -0.35, 0.42],
      [-1.3, 1.3, -1.0, 0.4],
      [1.3, 1.25, 0.35, 0.4],
      [1.38, 1.1, -0.35, 0.42],
      [1.3, 1.3, -1.0, 0.4],
    ];
    for (const [x, y, z, r] of falls) {
      const m = curl(x, y, z, r, 1, 1.75); // stretched DOWN: a falling lock
      this.pibeFalls.push({ mesh: m, base: m.position.clone() });
    }

    // Trailing locks: long curls streaming BACKWARD (-Z) and drooping — these
    // are the ones update() sways extra so the hair visibly FLOWS at speed.
    const locks: [number, number, number, number][] = [
      [-0.6, 1.7, -1.4, 0.48],
      [0.6, 1.7, -1.4, 0.48],
      [0, 2.05, -1.6, 0.52],
      [0, 1.5, -1.95, 0.46],
      [0, 1.75, -2.3, 0.4],
    ];
    for (const [x, y, z, r] of locks) {
      const m = curl(x, y, z, r, 1.8); // stretched along Z: a lock, not a ball
      this.pibeLocks.push({ mesh: m, base: m.position.clone() });
    }

    this.pibeHair.visible = false;
    // On `body` (not `group`) so the mop leans with the fake suspension —
    // rolling into a corner tips the hair with the car, which sells its weight.
    this.body.add(this.pibeHair);
  }

  /** The stun/spin-out cartoon rig (M58): three dizzy STARS that orbit above
   *  the cab (the universal "seeing stars" kart language) + four looping
   *  tyre-SMOKE puffs billowing off the wheels — all camera-facing sprites on
   *  two shared canvas textures, faded in/out by stunFade in update(). */
  private buildStunFx(): void {
    this.stunGroup = new THREE.Group();
    const starMat = new THREE.SpriteMaterial({
      map: starTexture(),
      color: 0xffe36e,
      transparent: true,
      depthWrite: false,
    });
    for (let i = 0; i < 3; i++) {
      const s = new THREE.Sprite(starMat);
      s.scale.set(0.55, 0.55, 1);
      this.stunStars.push(s);
      this.stunGroup.add(s);
    }
    for (let i = 0; i < 4; i++) {
      // Per-puff material: each puff fades on its own loop phase.
      const mat = new THREE.SpriteMaterial({
        map: smokeTexture(),
        color: 0xc9c9cc,
        transparent: true,
        opacity: 0,
        depthWrite: false,
      });
      const sprite = new THREE.Sprite(mat);
      this.stunPuffs.push({ sprite, mat, phase: i / 4 });
      this.stunGroup.add(sprite);
    }
    this.stunGroup.visible = false;
    this.group.add(this.stunGroup);
  }

  /** The racer is stunned/spun out (M58): dizzy stars + tyre smoke + wobble. */
  /** M115 — a bulldozer just rammed this car: throw it into TWO full flips and
   *  land it on its wheels facing down-course. `yawFix` is the shortest turn
   *  from the car's current heading to the track's forward direction. */
  triggerTumble(dur: number, yawFix: number): void {
    this.tumbleT = dur;
    this.tumbleDur = dur;
    this.tumbleYawFix = yawFix;
  }

  setStunned(on: boolean): void {
    this.stunTarget = on ? 1 : 0;
  }

  /** STUDS OUT (M59, reshaped M60b per owner): ONE big silver spike per wheel
   *  that points STRAIGHT OUT THE SIDE (perpendicular to the vehicle's flank,
   *  along ±X) and always keeps that outward direction. It's mounted off-centre
   *  on the wheel, so as the tyre spins the spike's base ORBITS around the axle
   *  (that's the only reason it "rotates") while the spike itself never changes
   *  which way it points — directly outward. Four total: one outboard of each
   *  wheel on a 4-wheeler; the MOTORBIKE (2 wheels on the centreline) gets one
   *  on EACH side of each tyre, still four. A side-facing spike sends a struck
   *  ball toward the near guardrail, never backward. */
  private buildStuds(): void {
    this.studsGroup = new THREE.Group();
    const mat = new THREE.MeshStandardMaterial({
      color: 0xe6ebf3,
      roughness: 0.18,
      metalness: 0.95,
      emissive: 0x3a4150,
      emissiveIntensity: 0.3,
    });
    // A long tapered spike built pointing +X (straight out the RIGHT side). The
    // base sits at x=0, the tip out at x≈0.95. We mirror scale.x for the left.
    const spikeGeo = new THREE.ConeGeometry(0.14, 0.95, 6);
    spikeGeo.rotateZ(-Math.PI / 2); // +Y → +X (points out the side)
    spikeGeo.translate(0.48, 0, 0); // base at the wheel face, tip out past it
    const collarGeo = new THREE.CylinderGeometry(0.15, 0.18, 0.14, 10); // base collar
    collarGeo.rotateZ(Math.PI / 2); // disc faces sideways (axle along X)

    const ORBIT = 0.34; // how far off-centre the spike rides (its orbit radius)
    // One hub = a group that SPINS about the axle (X). Its child spike is placed
    // ORBIT above centre and points ±X, so the spin orbits it while it keeps
    // pointing straight out the side.
    const makeHub = (cx: number, cy: number, cz: number, sideSign: number): void => {
      const hub = new THREE.Group();
      hub.position.set(cx, cy, cz);
      const spike = new THREE.Mesh(spikeGeo, mat);
      spike.position.set(sideSign * 0.14, ORBIT, 0); // outboard face, off-centre
      const collar = new THREE.Mesh(collarGeo, mat);
      collar.position.set(sideSign * 0.14, ORBIT, 0);
      if (sideSign < 0) {
        spike.scale.x = -1; // mirror to point out the LEFT side
        collar.scale.x = -1;
      }
      hub.add(spike, collar);
      this.studHubs.push(hub);
      this.studsGroup.add(hub);
    };

    if (this.vehicleId === 'motorcycle') {
      // Two wheels on the centreline; a spike out each side of each tyre.
      for (const cz of [1.1, -1.1]) {
        makeHub(0, 0.46, cz, 1); // right side
        makeHub(0, 0.46, cz, -1); // left side
      }
    } else {
      // Four wheels; one spike outboard of each (left wheels point left, etc.).
      for (const [cx, cz] of [
        [1.02, 1.32],
        [-1.02, 1.32],
        [1.02, -1.32],
        [-1.02, -1.32],
      ] as const) {
        makeHub(cx, 0.46, cz, Math.sign(cx));
      }
    }
    this.studsGroup.visible = false;
    this.group.add(this.studsGroup);
  }

  /** The anime "slam kick" impact burst (M60): a manga starburst sprite that
   *  pops in front of the car at the moment of a SCORPION KICK and fades. */
  private buildScorpionBurst(): void {
    this.scorpionBurstMat = new THREE.SpriteMaterial({
      map: scorpionBurstTexture(),
      color: 0xffffff,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.scorpionBurst = new THREE.Sprite(this.scorpionBurstMat);
    // High above the car — where the ball sits at its apex when the scorpion
    // volley connects (M60b two-phase kick).
    this.scorpionBurst.position.set(0, 4.6, 0.6);
    this.scorpionBurst.visible = false;
    this.group.add(this.scorpionBurst);
  }

  /** SLIPPERY MUD deploy spray (M60b): a pool of muddy blob sprites that get
   *  flung out the BACK of the car when the puddle is poured. */
  private buildMudSpray(): void {
    const tex = smokeTexture(); // a soft radial blob, tinted brown per sprite
    for (let i = 0; i < 10; i++) {
      const mat = new THREE.SpriteMaterial({
        map: tex,
        color: i % 2 === 0 ? 0x5a3d22 : 0x74522f, // two muddy browns
        transparent: true,
        opacity: 0,
        depthWrite: false,
      });
      const s = new THREE.Sprite(mat);
      s.visible = false;
      this.mudSpray.push({ sprite: s, vx: 0, vy: 0, vz: 0 });
      this.group.add(s);
    }
  }

  /** Fire the mud deploy spray (M60b): called when THIS car pours a puddle. The
   *  blobs launch backward + up with a lateral spread, then fall + fade. */
  triggerMud(): void {
    this.mudSprayTimer = 0.6;
    const n = this.mudSpray.length;
    for (let i = 0; i < n; i++) {
      const m = this.mudSpray[i];
      const spread = (i / (n - 1) - 0.5) * 1.4; // fan out across ±X
      m.sprite.position.set(spread * 0.6, 0.45, -1.9); // at the rear of the car
      m.vx = spread * (4 + (i % 3)); // spray outward…
      m.vy = 3.2 + (i % 4) * 0.5; // …up…
      m.vz = -(4.5 + (i % 5) * 0.6); // …and BACKWARD (-Z is the tail)
    }
  }

  /** STUDS OUT is running (M59): fade the spinning spikes in (and back out). */
  setStuds(on: boolean): void {
    this.studsTarget = on ? 1 : 0;
  }

  /** SCORPION KICK (M60b): raw seconds left on the car lock. update() derives
   *  the two phases from it — phase 1 (timer above flipTime) a grounded pop-up
   *  gesture while the ball rises, phase 2 (timer ≤ flipTime) the leap + flip
   *  volley. 0 = not active. */
  setScorpion(timerLeft: number): void {
    this.scorpionTimerLeft = timerLeft;
  }

  /** Power-up cue: ramp the colour aura + the boost effects while boosting. */
  setBoosting(on: boolean): void {
    if (on && this.boostTarget === 0) this.boostFx.ignite(); // the shock ring
    this.boostTarget = on ? 1 : 0;
  }

  /** MAGNET (M145): the field runs from this car onto its ball. `bx/by/bz` is
   *  the ball's centre in WORLD space (only read while `on`). */
  setMagnet(on: boolean, bx = 0, by = 0, bz = 0): void {
    this.magnetTarget = on ? 1 : 0;
    if (on) this.magnetBall.set(bx, by, bz);
  }

  /** KICK (M145): pop the impact burst at the ball (WORLD position). */
  triggerKick(bx: number, by: number, bz: number): void {
    this.group.worldToLocal(this.kickBurst.position.set(bx, by, bz));
    this.kickBurstTimer = KICK_BURST_TIME;
  }

  /** OFFSIDE TRAP (M145): the flag goes up over the car that called it. */
  triggerOffside(): void {
    this.offsideFlagTimer = OFFSIDE_FLAG_TIME;
  }

  /** The shield just turned a hit away (M145): flash the bubble. */
  triggerShieldBlock(): void {
    this.shield.block();
  }

  /** The dozer item is running: morph into the bulldozer (and back). */
  setDozer(on: boolean): void {
    this.dozerTarget = on ? 1 : 0;
  }

  /** The shield item is running: fade the bubble up (and back down). */
  setShield(on: boolean): void {
    this.shield.set(on);
  }

  /** M128: our ball is riding the nose (drives the Keeper's Gloves grab). */
  setDribbling(on: boolean): void {
    this.dribbling = on;
  }

  /** Release the GPU resources a rebuilt view leaves behind (its mods). */
  dispose(): void {
    this.modRig?.dispose();
    this.modRig = null;
  }

  /** PIBE DE ORO is running (M46): the golden glow fades up (and back down). */
  setPibe(on: boolean): void {
    this.pibeTarget = on ? 1 : 0;
  }

  /** Snap the smoothing to the snapshot (use once at the start, no slide-in). */
  warp(snap: CarRenderSnapshot): void {
    this.smoothY = snap.y;
    this.placed = true;
    this.group.position.set(snap.x, snap.y, snap.z);
    this.group.rotation.y = snap.heading;
  }

  update(snap: CarRenderSnapshot, dt: number): void {
    this.t += dt;
    // Position; soft vertical follow so hills don't jolt the body (§6.3).
    if (!this.placed) {
      this.smoothY = snap.y;
      this.placed = true;
    }
    // Soft suspension on bumps, but snap for a big delta (a ramp jump) so the car
    // visibly leaves the ground instead of lagging behind the launch.
    if (Math.abs(snap.y - this.smoothY) > 1.2) this.smoothY = snap.y;
    else this.smoothY = damp(this.smoothY, snap.y, tuning.car.verticalFollow, dt);
    this.group.position.set(snap.x, this.smoothY, snap.z);
    this.group.rotation.y = snap.heading; // §6.1: +heading

    // Fake suspension: roll from sideways speed, pitch from acceleration.
    const accel = (snap.speed - this.prevSpeed) / Math.max(dt, 1e-4);
    this.prevSpeed = snap.speed;
    this.accelSmooth = damp(this.accelSmooth, accel, 6, dt);
    this.modRig?.update(snap.speed, snap.lateralSpeed, this.accelSmooth, this.boostTarget > 0.5, this.dribbling, dt);
    const targetRoll = clamp(-snap.lateralSpeed * tuning.car.bodyRoll, -0.3, 0.3);
    const targetPitch = clamp(-accel * tuning.car.bodyPitch, -0.18, 0.18);
    this.roll = damp(this.roll, targetRoll, tuning.car.leanSmooth, dt);
    this.pitch = damp(this.pitch, targetPitch, tuning.car.leanSmooth, dt);
    // Stun wobble (M58): while spun out the body ROCKS on its suspension —
    // the physics yaw-spin plus this cosmetic shudder sells the loss of grip.
    this.stunFade = damp(this.stunFade, this.stunTarget, 9, dt);
    const wob = this.stunFade;
    this.body.rotation.set(
      this.pitch + Math.sin(this.t * 11) * 0.05 * wob,
      0,
      this.roll + Math.sin(this.t * 13 + 1) * 0.07 * wob,
    );
    // BULLDOZER TUMBLE (M115, owner: "make that car do 2 flips in the air before
    // returning back down and landing on it's tires ... ensure the car lands
    // facing forwards on the track"). Runs BEFORE the scorpion block so a
    // scorpion volley (which is intangible and can't be dozed) always wins.
    if (this.tumbleT > 0) {
      this.tumbleT = Math.max(0, this.tumbleT - dt);
      const p = 1 - this.tumbleT / this.tumbleDur; // 0 → 1 across the flight
      // TWO full flips, exactly — landing on its tyres is the whole point, so
      // the rotation has to finish on a whole turn.
      const flip = p * Math.PI * 2 * 2;
      // A launch arc that starts fast and lands soft (sin gives the hang time).
      const arc = Math.sin(Math.min(1, p) * Math.PI);
      // Ease the yaw correction in so touchdown is square with the road, then
      // let it bleed off as control returns (a hard snap back reads broken).
      const yawEase = p * p * (3 - 2 * p); // smoothstep
      this.body.rotation.set(flip, this.tumbleYawFix * yawEase, this.roll * (1 - arc));
      this.body.position.y = arc * 3.1;
    } else if (this.tumbleYawFix !== 0) {
      // Landed: unwind the visual correction over a few frames.
      this.tumbleYawFix = damp(this.tumbleYawFix, 0, 6, dt);
      this.body.rotation.y = this.tumbleYawFix;
      if (Math.abs(this.tumbleYawFix) < 0.005) this.tumbleYawFix = 0;
    }

    // SCORPION KICK (M60b): a TWO-PHASE bicycle kick, driven by the raw car-lock
    // timer. Phase 1 (timer above flipTime): the car stays grounded and gives a
    // quick nose-flick "pop" as the ball launches straight up. Phase 2 (timer ≤
    // flipTime = the ball's apex): the car LEAPS and FRONT-flips (~1.2 turns +
    // a yaw twist) — the scorpion volley — then drops onto its spot. The anime
    // slam-kick burst fires at the phase-2 boundary (the actual kick).
    // M60c (owner): a FRONT flip (nose dives, the BACK of the car sweeps up and
    // over the top to strike the ball forward), so rotation.x goes POSITIVE
    // (positive pitch = nose-down, per the brake-dive convention above).
    const scDur = tuning.powerups.scorpion;
    if (this.scorpionTimerLeft > scDur.flipTime) {
      // Phase 1 — the pop-up gesture (grounded, nose flicks up then settles).
      const kp = 1 - (this.scorpionTimerLeft - scDur.flipTime) / scDur.kickUpTime; // 0→1
      this.body.rotation.x = this.pitch - Math.sin(Math.min(1, kp) * Math.PI) * 0.5;
      this.body.position.y = 0;
      this.scorpionFlipStarted = false;
    } else if (this.scorpionTimerLeft > 0) {
      // Phase 2 — the leap + FRONT flip volley (tail comes over the top).
      if (!this.scorpionFlipStarted) {
        this.scorpionBurstTimer = 0.55; // pop the impact graphic at the kick
        this.scorpionFlipStarted = true;
      }
      const p = 1 - this.scorpionTimerLeft / scDur.flipTime; // 0→1 over the flip
      const flip = p * Math.PI * 2 * 1.2; // ~1.2 FRONT flips
      const arc = Math.sin(Math.min(1, p) * Math.PI); // 0 → 1 → 0
      this.body.rotation.set(flip, arc * 0.5, 0); // FRONT flip (nose down + over) + a yaw twist
      this.body.position.y = arc * 2.6; // a HIGH leap
    } else {
      this.scorpionFlipStarted = false;
      // M115: don't yank a live bulldozer tumble back to the ground.
      if (this.body.position.y !== 0 && this.tumbleT <= 0) this.body.position.y = 0;
    }
    // The anime impact burst: expand, spin, and fade over its short life.
    if (this.scorpionBurstTimer > 0) {
      this.scorpionBurstTimer = Math.max(0, this.scorpionBurstTimer - dt);
      const life = this.scorpionBurstTimer / 0.55; // 1 → 0
      this.scorpionBurst.visible = true;
      const bs = 1.6 + (1 - life) * 5.0; // expands 1.6 → 6.6
      this.scorpionBurst.scale.set(bs, bs, 1);
      this.scorpionBurstMat.opacity = life;
      this.scorpionBurstMat.rotation = (1 - life) * 1.1; // a slight rotate as it blooms
    } else if (this.scorpionBurst.visible) {
      this.scorpionBurst.visible = false;
    }

    // SLIPPERY MUD deploy spray (M60b): ballistic mud blobs out the back.
    if (this.mudSprayTimer > 0) {
      this.mudSprayTimer = Math.max(0, this.mudSprayTimer - dt);
      const k = this.mudSprayTimer / 0.6; // 1 → 0
      for (const m of this.mudSpray) {
        m.sprite.visible = true;
        m.vy -= 14 * dt; // gravity on the blobs
        m.sprite.position.x += m.vx * dt;
        m.sprite.position.y += m.vy * dt;
        m.sprite.position.z += m.vz * dt;
        if (m.sprite.position.y < 0.12) m.sprite.position.y = 0.12; // splat onto the road
        const sc = 0.45 + (1 - k) * 0.7; // grows as it spreads
        m.sprite.scale.set(sc, sc, 1);
        (m.sprite.material as THREE.SpriteMaterial).opacity = k * 0.9;
      }
    } else if (this.mudSpray.length > 0 && this.mudSpray[0].sprite.visible) {
      for (const m of this.mudSpray) m.sprite.visible = false;
    }

    // Wheels: spin with speed, front pair visually steers.
    this.wheelSpin += (snap.speed * dt) / 0.46;
    for (const w of this.wheels) w.rotation.x = this.wheelSpin;
    for (const p of this.steerPivots) p.rotation.y = snap.steerAngle * 0.5;
    // A loaded GLB's own wheels roll about their axle (procedural ones hidden).
    for (const w of this.wheelPivots) w.pivot.quaternion.setFromAxisAngle(w.axis, this.wheelSpin);

    // Boost: aura flare + tail-light surge + afterburners, trails and wind.
    this.boostFlare = damp(this.boostFlare, this.boostTarget, 10, dt);
    this.auraMat.opacity = this.auraBaseOpacity + this.boostFlare * 0.5;
    const auraScale = 1 + this.boostFlare * 0.35;
    this.aura.scale.set(auraScale, auraScale, auraScale);
    this.tailMat.emissiveIntensity = 0.8 + this.boostFlare * 1.6;
    this.boostFx.update(this.boostFlare, dt);

    // MAGNET (M145): the field from the car onto its ball. The ball arrives
    // in world space; the field lives in this group, so bring it local (the
    // group only ever carries a position + a yaw).
    this.magnetFade = damp(this.magnetFade, this.magnetTarget, 9, dt);
    if (this.magnetFade > 0.02) {
      const dx = this.magnetBall.x - this.group.position.x;
      const dz = this.magnetBall.z - this.group.position.z;
      const sin = Math.sin(snap.heading);
      const cos = Math.cos(snap.heading);
      this.magnetField.update(
        this.magnetFade,
        dx * cos - dz * sin,
        this.magnetBall.y - this.group.position.y,
        dx * sin + dz * cos,
        dt,
      );
    } else if (this.magnetField.group.visible) {
      this.magnetField.group.visible = false;
    }

    // KICK (M145): the impact burst blooms where the ball was struck.
    if (this.kickBurstTimer > 0) {
      this.kickBurstTimer = Math.max(0, this.kickBurstTimer - dt);
      const life = this.kickBurstTimer / KICK_BURST_TIME; // 1 → 0
      const ks = 1.4 + (1 - life) * 4.2;
      this.kickBurst.visible = true;
      this.kickBurst.scale.set(ks, ks, 1);
      this.kickBurstMat.opacity = life;
      this.kickBurstMat.rotation = (1 - life) * 0.9;
    } else if (this.kickBurst.visible) {
      this.kickBurst.visible = false;
    }

    // OFFSIDE TRAP (M145): the flag snaps up with an overshoot, waves, fades.
    if (this.offsideFlagTimer > 0) {
      this.offsideFlagTimer = Math.max(0, this.offsideFlagTimer - dt);
      const age = OFFSIDE_FLAG_TIME - this.offsideFlagTimer;
      const up = Math.min(1, age / 0.16);
      const pop = up * (1 + 0.35 * (1 - up)); // slight overshoot on the way up
      const fs = 3.4 * pop;
      this.offsideFlag.visible = true;
      this.offsideFlag.position.set(0.2, 1.5, -0.2);
      this.offsideFlag.scale.set(fs, fs, 1);
      this.offsideFlagMat.rotation = Math.sin(age * 16) * 0.16 * up; // the wave
      this.offsideFlagMat.opacity = Math.min(1, this.offsideFlagTimer / 0.3);
    } else if (this.offsideFlag.visible) {
      this.offsideFlag.visible = false;
    }

    // Bulldozer transformation: a quick scale-pop crossfade between the forms.
    this.dozerBlend = damp(this.dozerBlend, this.dozerTarget, 9, dt);
    const db = this.dozerBlend;
    this.dozerGroup.visible = db > 0.03;
    this.sportGroup.visible = db < 0.97;
    if (this.dozerGroup.visible) {
      const s = Math.max(0.05, db * (1 + 0.18 * (1 - db))); // slight overshoot pop
      this.dozerGroup.scale.set(s, s, s);
    }
    if (this.sportGroup.visible) {
      const s = Math.max(0.05, 1 - db);
      this.sportGroup.scale.set(s, s, s);
    }

    // Shield bubble (M145: fx/ShieldBubble.ts — rim glow, honeycomb, pop, flash).
    this.shield.update(dt);

    // STUDS OUT (M59): the spiked hubs fade in and SPIN with the wheels.
    this.studsFade = damp(this.studsFade, this.studsTarget, 8, dt);
    this.studsGroup.visible = this.studsFade > 0.02;
    if (this.studsGroup.visible) {
      const ss = Math.max(0.05, this.studsFade);
      for (const h of this.studHubs) {
        h.rotation.x = this.wheelSpin; // roll with the wheels
        h.scale.setScalar(ss);
      }
    }

    // Pibe de Oro (M58b — bubble-free): the Maradona mop pops in with a
    // slight overshoot (like the dozer transform) and, while visible, FLOWS —
    // the whole mop bobs/sways, and the trailing back locks whip harder and
    // STREAM backward with speed (stretched + pushed like hair in the wind),
    // each on its own phase so they never move in lockstep.
    this.pibeFade = damp(this.pibeFade, this.pibeTarget, 8, dt);
    this.pibeHair.visible = this.pibeFade > 0.02;
    if (this.pibeHair.visible) {
      const hs = Math.max(0.05, this.pibeFade * (1 + 0.18 * (1 - this.pibeFade)));
      this.pibeHair.scale.set(hs, hs, hs);
      this.pibeHair.position.y = Math.sin(this.t * 3.1) * 0.08; // gentle bob
      this.pibeHair.rotation.z = Math.sin(this.t * 2.3) * 0.05; // side sway
      this.pibeHair.rotation.x = Math.sin(this.t * 1.7 + 1.3) * 0.035; // nod
      const flow = Math.min(1, snap.speed / 40); // 0 parked → 1 flat out
      for (let i = 0; i < this.pibeLocks.length; i++) {
        const lock = this.pibeLocks[i];
        if (!lock) continue;
        lock.mesh.position.y = lock.base.y + Math.sin(this.t * 4.2 + i * 1.7) * 0.16;
        lock.mesh.position.z =
          lock.base.z -
          flow * 0.4 + // streamed backward at speed
          Math.sin(this.t * 3.4 + i * 2.1) * (0.11 + flow * 0.08); // whips harder too
        lock.mesh.scale.z = lock.mesh.scale.x * (1.8 + flow * 0.9); // wind-stretched
      }
      // The side falls swing like pendulums (out from the flanks and back),
      // and lean backward a touch as speed builds — hair, not panniers.
      for (let i = 0; i < this.pibeFalls.length; i++) {
        const fall = this.pibeFalls[i];
        if (!fall) continue;
        const side = Math.sign(fall.base.x);
        fall.mesh.position.x = fall.base.x + side * Math.sin(this.t * 3.6 + i * 2.3) * 0.06;
        fall.mesh.position.z = fall.base.z - flow * 0.3;
        fall.mesh.position.y = fall.base.y + Math.sin(this.t * 4.6 + i * 1.9) * 0.05;
      }
      // The glow lives IN the hair — a deep dark↔gold PULSE on the curl
      // surfaces (the Mario-star flash: at the trough you see dark Diego
      // curls, at the peak they burn gold) + a whisper-faint rim aura. The
      // rims MUST stay near-invisible individually: ~25 shells overlap 3-4
      // deep, so opacity stacks — 0.16 each already washed the mop into a
      // translucent gold blob (measured). No dome.
      this.pibeHairMat.emissiveIntensity = (0.35 + Math.sin(this.t * 6) * 0.24) * this.pibeFade;
      this.pibeHairGlowMat.opacity = (0.07 + Math.sin(this.t * 6 + 0.7) * 0.03) * this.pibeFade;
    }

    // Spin-out FX (M58): dizzy stars circle over the cab, tyre smoke loops
    // off the wheels. stunFade was advanced above (it also drives the wobble).
    this.stunGroup.visible = this.stunFade > 0.03;
    if (this.stunGroup.visible) {
      for (let i = 0; i < this.stunStars.length; i++) {
        const s = this.stunStars[i];
        if (!s) continue;
        const a = this.t * 6.5 + (i * Math.PI * 2) / this.stunStars.length;
        s.position.set(
          Math.cos(a) * 1.25,
          2.2 + Math.sin(this.t * 4 + i) * 0.12,
          Math.sin(a) * 1.25,
        );
        const sc = (0.5 + 0.12 * Math.sin(this.t * 8 + i * 2)) * this.stunFade;
        s.scale.set(sc, sc, 1);
      }
      for (let i = 0; i < this.stunPuffs.length; i++) {
        const puff = this.stunPuffs[i];
        if (!puff) continue;
        puff.phase += dt * 1.4;
        if (puff.phase >= 1) puff.phase -= 1;
        const k = puff.phase;
        // Each puff kicks off a rear wheel corner, swells + rises + fades.
        const side = i % 2 === 0 ? 1 : -1;
        puff.sprite.position.set(
          side * (0.95 + Math.sin(i * 3.7) * 0.2),
          0.35 + k * 1.2,
          -1.1 + Math.cos(i * 2.9) * 0.3 - k * 0.9,
        );
        const sc = 0.7 + k * 2.0;
        puff.sprite.scale.set(sc, sc, 1);
        puff.mat.opacity = (1 - k) * 0.4 * this.stunFade;
      }
    }
  }
}

const KICK_BURST_TIME = 0.42;
const OFFSIDE_FLAG_TIME = 1.25;

// ---- The linesman's flag (M145) — the OFFSIDE TRAP's pop-up. A chequered
// yellow-and-red flag on a stick, drawn once and shared by every car. --------
let offsideFlagTex: THREE.CanvasTexture | null = null;
function offsideFlagTexture(): THREE.CanvasTexture {
  if (offsideFlagTex) return offsideFlagTex;
  const c = document.createElement('canvas');
  c.width = 128;
  c.height = 128;
  const x = c.getContext('2d') as CanvasRenderingContext2D;
  x.shadowColor = 'rgba(0,0,0,0.55)';
  x.shadowBlur = 6;
  // The stick, leaning a little.
  x.save();
  x.translate(24, 118);
  x.rotate(-0.12);
  x.fillStyle = '#f4f1e6';
  x.fillRect(-3, -112, 6, 112);
  // The cloth: four quarters, yellow and red, with a wavy trailing edge.
  const W = 78;
  const H = 58;
  const quarter = (qx: number, qy: number, col: string): void => {
    x.fillStyle = col;
    x.beginPath();
    const x0 = 3 + qx * (W / 2);
    const y0 = -110 + qy * (H / 2);
    x.moveTo(x0, y0);
    x.lineTo(x0 + W / 2, y0 + (qx === 1 ? 4 : 2));
    x.lineTo(x0 + W / 2, y0 + H / 2 + (qx === 1 ? 4 : 2));
    x.lineTo(x0, y0 + H / 2);
    x.closePath();
    x.fill();
  };
  quarter(0, 0, '#ffd21f');
  quarter(1, 0, '#e8312b');
  quarter(0, 1, '#e8312b');
  quarter(1, 1, '#ffd21f');
  x.restore();
  offsideFlagTex = new THREE.CanvasTexture(c);
  offsideFlagTex.colorSpace = THREE.SRGBColorSpace;
  offsideFlagTex.userData.shared = true;
  return offsideFlagTex;
}

// ---- Spin-out sprite textures (M58) — shared lazy singletons: every CarView
// reuses the same star + smoke canvases (built on first construction). -------

let dizzyStarTex: THREE.CanvasTexture | null = null;
function starTexture(): THREE.CanvasTexture {
  if (dizzyStarTex) return dizzyStarTex;
  const c = document.createElement('canvas');
  c.width = 64;
  c.height = 64;
  const x = c.getContext('2d') as CanvasRenderingContext2D;
  x.translate(32, 32);
  x.beginPath();
  for (let i = 0; i < 10; i++) {
    const r = i % 2 === 0 ? 26 : 11; // alternating outer/inner = 5-point star
    const a = (i / 10) * Math.PI * 2 - Math.PI / 2;
    if (i === 0) x.moveTo(Math.cos(a) * r, Math.sin(a) * r);
    else x.lineTo(Math.cos(a) * r, Math.sin(a) * r);
  }
  x.closePath();
  x.shadowColor = '#ffd94a';
  x.shadowBlur = 8;
  x.fillStyle = '#fff3b8';
  x.fill();
  dizzyStarTex = new THREE.CanvasTexture(c);
  dizzyStarTex.colorSpace = THREE.SRGBColorSpace;
  return dizzyStarTex;
}

let smokeBlobTex: THREE.CanvasTexture | null = null;
function smokeTexture(): THREE.CanvasTexture {
  if (smokeBlobTex) return smokeBlobTex;
  const c = document.createElement('canvas');
  c.width = 64;
  c.height = 64;
  const x = c.getContext('2d') as CanvasRenderingContext2D;
  const g = x.createRadialGradient(32, 32, 4, 32, 32, 30);
  g.addColorStop(0, 'rgba(255,255,255,0.9)');
  g.addColorStop(0.6, 'rgba(255,255,255,0.35)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  x.fillStyle = g;
  x.fillRect(0, 0, 64, 64);
  smokeBlobTex = new THREE.CanvasTexture(c);
  return smokeBlobTex;
}

// ---- Anime slam-kick impact burst (M60) — a manga "POW" starburst: bold
// radial speed-lines behind a jagged white flash-star, hot at the centre. ----
let scorpionBurstTex: THREE.CanvasTexture | null = null;
function scorpionBurstTexture(): THREE.CanvasTexture {
  if (scorpionBurstTex) return scorpionBurstTex;
  const c = document.createElement('canvas');
  c.width = 256;
  c.height = 256;
  const x = c.getContext('2d') as CanvasRenderingContext2D;
  x.translate(128, 128);
  // Radial speed-lines (manga concentration burst) — spokes fading outward.
  const SPOKES = 28;
  for (let i = 0; i < SPOKES; i++) {
    const a = (i / SPOKES) * Math.PI * 2;
    const w = 0.05 + (0.06 * ((i * 7) % 5)) / 4; // jittered spoke widths
    const grad = x.createLinearGradient(0, 0, Math.cos(a) * 128, Math.sin(a) * 128);
    grad.addColorStop(0, 'rgba(255,240,190,0.0)');
    grad.addColorStop(0.45, 'rgba(255,235,170,0.85)');
    grad.addColorStop(1, 'rgba(255,210,120,0)');
    x.fillStyle = grad;
    x.beginPath();
    x.moveTo(0, 0);
    x.lineTo(Math.cos(a - w) * 128, Math.sin(a - w) * 128);
    x.lineTo(Math.cos(a + w) * 128, Math.sin(a + w) * 128);
    x.closePath();
    x.fill();
  }
  // A jagged flash-star in the middle (12-point spiky burst).
  x.beginPath();
  for (let i = 0; i < 24; i++) {
    const r = i % 2 === 0 ? 74 : 30;
    const a = (i / 24) * Math.PI * 2 - Math.PI / 2;
    if (i === 0) x.moveTo(Math.cos(a) * r, Math.sin(a) * r);
    else x.lineTo(Math.cos(a) * r, Math.sin(a) * r);
  }
  x.closePath();
  x.shadowColor = '#ffcf6a';
  x.shadowBlur = 22;
  x.fillStyle = '#fffbe8';
  x.fill();
  // A hot white core.
  const core = x.createRadialGradient(0, 0, 2, 0, 0, 34);
  core.addColorStop(0, 'rgba(255,255,255,1)');
  core.addColorStop(1, 'rgba(255,255,255,0)');
  x.fillStyle = core;
  x.beginPath();
  x.arc(0, 0, 34, 0, Math.PI * 2);
  x.fill();
  scorpionBurstTex = new THREE.CanvasTexture(c);
  scorpionBurstTex.colorSpace = THREE.SRGBColorSpace;
  return scorpionBurstTex;
}

/**
 * Re-material a loaded GLB vehicle with the game's PBR look (M35 Stage 2): the
 * racer's clearcoat paint on the body, dark reflective glass, matte rubber tyres
 * and polished rims. Parts are matched by their Blender material/node names
 * ("Paint" / "Glass" / "Tyre" / "Rim" / "Wheel"), so the in-engine materials
 * (which react to the IBL + bloom) replace whatever the model shipped with.
 */
// Exported for the M63 race-lobby showroom (LobbyStage), which renders the same
// GLBs with the same racer-colour paint outside a race.
/** M128: is this racer dribbling — the ball just off its nose? The render-side
 *  cousin of the sim's possession touch, used only to pose the gloves. */
export function ballOnNose(car: CarRenderSnapshot, ball: { x: number; z: number }, vehicleId = ''): boolean {
  const dx = ball.x - car.x;
  const dz = ball.z - car.z;
  const fwd = dx * Math.sin(car.heading) + dz * Math.cos(car.heading);
  // M153: the reach follows the ride's own nose (its ball-contact box).
  const reach = vehicleBoxFor(vehicleId).halfLength + tuning.ball.radius + 1.4;
  return fwd > 0 && dx * dx + dz * dz < reach * reach;
}

/** The tyre rubber's finish, shared by GLB models and the procedural fallback
 *  body. M154 (owner decision 2026-10-24): STOCK tyres are a lighter charcoal
 *  with a soft sheen so the tread and sidewall read against the dark arches
 *  (they were a near-black 0x0c0d10 that rendered as solid discs). Tire sets
 *  keep their own colour and the earlier matte finish; metal sets stay metal. */
export function tyreFinish(look?: Pick<CarLook, 'tyre' | 'tyreMetal'>): {
  color: number;
  roughness: number;
  metalness: number;
  envMapIntensity: number;
} {
  if (look?.tyreMetal) return { color: look.tyre ?? 0x2a2c31, roughness: 0.26, metalness: 0.9, envMapIntensity: 1.6 };
  if (look?.tyre !== undefined) return { color: look.tyre, roughness: 0.82, metalness: 0, envMapIntensity: 1.0 };
  return { color: 0x2a2c31, roughness: 0.62, metalness: 0, envMapIntensity: 1.4 };
}

export function applyCarMaterials(
  root: THREE.Object3D,
  color: number,
  look?: CarLook,
  /** M114b — the ride being dressed. Optional (a missing id just means "no
   *  stock treatment"), but every call site passes it so the Formula wears its
   *  chrome→blue gradient in the garage, the shop, the lobby and on track. */
  vehicleId?: string,
): void {
  // M114b: a per-vehicle STOCK paint treatment (today: the Formula's
  // chrome→blue gradient). Equipped body cosmetics beat it by construction —
  // stockPaintFor returns undefined whenever a wrap/kit is on the car.
  const stock = stockPaintFor(vehicleId ?? '', look);
  // M79 cosmetics: a wrap rides as the paint's texture map (colour stays
  // white so the map's own colours read true); paint jobs recolour body/trim;
  // tire sets recolour rubber + rims. All display-only.
  // M127: the livery is painted FOR THIS BODY — measureLivery finds the
  // painted shell (not the tyres/axles under it) and its belt line, and the
  // texture cache hands back the one canvas for that item + that fit.
  const measure = look?.livery || stock ? measureLivery(root, vehicleId) : null;
  const paintMap =
    look?.livery && measure ? liveryTexture(look.livery, measure) : (look?.wrapMap ?? stock?.map ?? null);
  // M152: the stock paint is a METALLIC FLAKE under the clearcoat — a fine
  // tiled normal map of random facets on the base coat (the coat itself
  // stays glass-smooth), so the body sparkles under the IBL instead of
  // reading as one flat glossy plastic (the owner's "texture of paint").
  // Wraps and kits keep their own flat base (their art is the surface).
  const flake = paintMap ? null : paintFlakeTexture();
  const paint = new THREE.MeshPhysicalMaterial({
    color: paintMap ? 0xffffff : (look?.body ?? color),
    map: paintMap,
    normalMap: flake,
    normalScale: new THREE.Vector2(0.38, 0.38),
    roughness: stock?.roughness ?? (flake ? 0.36 : 0.32),
    metalness: stock?.metalness ?? (flake ? 0.62 : 0.5),
    clearcoat: 1.0,
    clearcoatRoughness: flake ? 0.06 : 0.08,
    envMapIntensity: stock?.envMapIntensity ?? 1.3,
  });
  if (flake) {
    flake.repeat.set(26, 26);
    flake.needsUpdate = true;
  }
  // M144: a kit paint job is a THREE-VIEW atlas (side / top / nose / tail) —
  // the material learns to pick the view per pixel from the surface normal.
  const threeView = paintMap?.userData.lv3 === true;
  if (threeView) {
    patchLiveryMaterial(paint, {
      data: paintMap!.userData.lvData as THREE.Texture | undefined,
      mirror: paintMap!.userData.lvMirror === true,
      bump: paintMap!.userData.lvBump as number | undefined,
      glow: paintMap!.userData.lvGlow as number | undefined,
    });
    const coat = paintMap!.userData.lvClearcoat as number | undefined;
    if (coat !== undefined) paint.clearcoat = coat;
  }
  // M154 polish r5: a flag wrap with a plan-view decal (the Formula's crests
  // on its sidepod tops, its canton's stars on the nose top)
  const decal = !threeView ? (paintMap?.userData.lvDecal as THREE.Texture | undefined) : undefined;
  if (decal) patchWrapDecal(paint, decal, (paintMap!.userData.lvDecalMinY as number | undefined) ?? 0);
  const glass = new THREE.MeshPhysicalMaterial({
    color: 0x0a0e18,
    roughness: 0.06,
    metalness: 0.1,
    clearcoat: 1.0,
    clearcoatRoughness: 0.04,
    envMapIntensity: 1.6,
  });
  // M114 metal tires (Golden Boot / Quicksilver): the rubber ITSELF renders as
  // polished metal — high metalness + tight roughness + an env boost so the
  // band reads as solid gold/silver against the night IBL instead of matte
  // rubber (a matte gold just reads brown). Non-metal keeps the classic
  // matte-rubber values verbatim.
  // M144 painted wheels (Dragon Claws): tyre AND rim wear one painted wheel
  // face with its own relief, lacquered — polished bone, so it shines.
  const art = look?.wheelArt ? wheelArt(look.wheelArt) : null;
  const painted = art
    ? new THREE.MeshPhysicalMaterial({
        color: 0xffffff,
        map: art.map,
        bumpMap: art.bump,
        bumpScale: 2.2,
        roughness: 0.3,
        metalness: 0.08,
        clearcoat: 1.0,
        clearcoatRoughness: 0.1,
        envMapIntensity: 1.5,
      })
    : null;
  // M154: under an art set the stock rim (spokes, hub, lip ring) and the rim
  // accent rings hide and each wheel gets a face disc at its bead; any other
  // look shows them again (the shop preview re-dresses one model).
  fitArtWheels(root, painted !== null);
  if (painted) projectWheelFaces(root);
  const tyre =
    painted ?? new THREE.MeshStandardMaterial(tyreFinish(look));
  // M151: with no tire set equipped the rims wear the VehicleDef's stock
  // finish (the pack's dark gunmetal, or the muscle car's chrome).
  const stockRim = vehicleId ? VEHICLES[vehicleId] : undefined;
  const rimChrome = look?.rimChrome ?? (look?.rim === undefined && stockRim?.rimChrome === true);
  // M154: a stock DARK rim (gunmetal, gloss black) is wheel PAINT, not bare
  // metal: a lacquered metallic over the dark base. As a 0.92-metal mirror its
  // dark colour reflected ~4% of the light, the spokes went black against the
  // black barrel and every wheel read as a flat disc (in-game close-up). The
  // clear coat's white highlights trace the spokes whatever the base colour.
  const darkStockRim =
    !rimChrome && look?.rim === undefined && stockRim?.rimColor !== undefined &&
    new THREE.Color(stockRim.rimColor).getHSL({ h: 0, s: 0, l: 0 }).l < 0.4;
  const rim =
    painted ??
    (darkStockRim
      ? new THREE.MeshPhysicalMaterial({
          color: stockRim!.rimColor,
          roughness: 0.34,
          metalness: 0.45,
          clearcoat: 1.0,
          clearcoatRoughness: 0.08,
          envMapIntensity: 1.3,
        })
      : new THREE.MeshStandardMaterial({
          color: look?.rim ?? stockRim?.rimColor ?? 0xd2d8e2,
          roughness: rimChrome ? 0.06 : 0.18,
          metalness: rimChrome ? 1.0 : 0.92,
          envMapIntensity: rimChrome ? 1.8 : 1.4,
        }));
  // M154: the brake disc is cast iron, a mid grey with a soft sheen. As
  // chrome it mirrored whatever sat behind the wheel (black in the lab and
  // the night cities) and hid the spokes in front of it.
  const discIron = new THREE.MeshStandardMaterial({
    color: 0x303237,
    roughness: 0.58,
    metalness: 0.7,
    envMapIntensity: 0.8,
  });
  // M154: the concept pack's finishes, opt-in by name prefix (carLook
  // PREFIX_PARTS). Deep red rather than the shared tail material's coral
  // (0xff2233 x1.5 tone-maps salmon), a smoked lens that is mostly dark
  // glass, a cool-white LED, amber, satin charcoal, see-through glass and
  // a red caliper that is not body paint.
  const lens = new THREE.MeshPhysicalMaterial({
    color: 0x2a0608,
    roughness: 0.08,
    metalness: 0.1,
    clearcoat: 1.0,
    clearcoatRoughness: 0.04,
    // (a faint red glow inside the lens: the pack's lamps read lit across
    // their whole housing, a thin LED line alone dimmed the race-camera read)
    emissive: 0x6a0610,
    emissiveIntensity: 0.9,
    envMapIntensity: 1.4,
  });
  const ledRed = new THREE.MeshStandardMaterial({ color: 0x2a0000, emissive: 0xff0410, emissiveIntensity: 1.2, roughness: 0.4 });
  const ledCool = new THREE.MeshStandardMaterial({ color: 0xdfeeff, emissive: 0xcfe6ff, emissiveIntensity: 1.6, roughness: 0.3 });
  const amber = new THREE.MeshStandardMaterial({ color: 0x7a4200, emissive: 0xff8a00, emissiveIntensity: 0.45, roughness: 0.35 });
  const charcoal = new THREE.MeshStandardMaterial({ color: 0x3a3d42, roughness: 0.55, metalness: 0.3, envMapIntensity: 0.9 });
  // (M154 r5) a light satin steel plate (Satin_): mostly diffuse, so it
  // shades with the lights and never mirrors a city's sky; a hair warm of
  // neutral so the cool studio light does not turn it blue
  const satin = new THREE.MeshStandardMaterial({ color: 0xa4a29e, roughness: 0.55, metalness: 0.1, envMapIntensity: 0.45 });
  const glassClear = new THREE.MeshPhysicalMaterial({
    color: 0x9fb4c8,
    transparent: true,
    opacity: 0.32,
    roughness: 0.04,
    metalness: 0.0,
    clearcoat: 1.0,
    envMapIntensity: 1.5,
    depthWrite: false,
  });
  const brake = new THREE.MeshPhysicalMaterial({ color: 0xc8141c, roughness: 0.35, metalness: 0.25, clearcoat: 0.6 });
  // Emissive lamps (these bloom), bright chrome, and dark trim/bumpers (M35 detail pass).
  const head = new THREE.MeshStandardMaterial({
    color: 0xfff7e0,
    emissive: 0xffe9b0,
    emissiveIntensity: 1.7,
    roughness: 0.3,
  });
  const tail = new THREE.MeshStandardMaterial({
    color: 0x40060a,
    emissive: 0xff2233,
    emissiveIntensity: 1.5,
    roughness: 0.4,
  });
  const chrome = new THREE.MeshStandardMaterial({
    color: 0xe2e7ef,
    roughness: 0.14,
    metalness: 1.0,
    envMapIntensity: 1.6,
  });
  // M151: gold anodised metal (the hyperbike's inverted forks).
  const gold = new THREE.MeshStandardMaterial({
    color: 0xc9a24a,
    roughness: 0.26,
    metalness: 0.95,
    envMapIntensity: 1.5,
  });
  // (M153: less metal and less sky in it: at 0.3 metal the big flat dark
  // trim, the Formula's floor above all, mirrored the sky as slate grey;
  // the briefs ask for black)
  const trim = new THREE.MeshStandardMaterial({
    color: look?.trim ?? 0x0b0c11,
    roughness: 0.58,
    metalness: 0.15,
    envMapIntensity: 0.75,
  });
  // M153: the trim that stays dark under every look (cavities, the glass
  // frames and pillars, the rider's suit, grilles and mesh: carLook.trimIsDark).
  const trimDark = look?.trim === undefined
    ? trim
    : new THREE.MeshStandardMaterial({ color: 0x0b0c11, roughness: 0.58, metalness: 0.15, envMapIntensity: 0.75 });
  // M153: a wheel's barrel (behind the spokes) under a coloured rim (a tire
  // set, a kit's socks) is the rim in shadow, so the spokes stand off it; a
  // dark rim (black or navy socks) gets a gunmetal barrel instead. A barrel
  // in the shorts colour made blank white discs (Juventus) and black holes
  // (dark-shorts kits), and one in the full rim colour flat hubcaps (two
  // review rounds).
  const barrel = (() => {
    if (look?.rim === undefined) {
      // M154: a stock barrel the pack paints (the monster truck's red wheel
      // centres behind black spokes), a satin finish so it reads as paint
      const bc = stockRim?.barrelColor;
      return bc === undefined
        ? trim
        : new THREE.MeshStandardMaterial({ color: bc, roughness: 0.45, metalness: 0.35, envMapIntensity: 1.0 });
    }
    const lumaOf = (c: THREE.Color): number => 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b;
    const rc = new THREE.Color(look.rim);
    const luma = lumaOf(rc);
    // A kit's own wheels (no tire set over them) on a car: the SHORTS behind
    // the SOCKS wherever the shorts are a clearly different colour and not
    // the lighter of the two (Penarol's gold spokes on black, River's red on
    // black, England's red on royal blue). Round 3 put every kit on the
    // rim-shade rule below and those wheels went flat. Not on the motorbike:
    // its wheels are discs, and a black face read as an empty ring (round 4).
    if (look.tyre === undefined && look.trim !== undefined && luma > 0.08 && vehicleId !== 'motorcycle') {
      const tc = new THREE.Color(look.trim);
      const ra = new THREE.Color(look.rim);
      const apart = Math.max(Math.abs(tc.r - ra.r), Math.abs(tc.g - ra.g), Math.abs(tc.b - ra.b));
      if (lumaOf(tc) <= luma && apart > 0.25) return trim;
    }
    // A share of the rim's light. On a car the barrel is the shadow BEHIND
    // the spokes, so it stays well under the rim (at 50% silver and gold
    // spokes faded into it and the wheel read as a flat hubcap, round 3).
    // The motorbike's wheels are discs: its barrel IS the wheel's face, so it
    // wears most of the set's colour (at 30% white read dark grey and gold
    // brown, round 2).
    // (a coloured rim at 46% read as a flat red disc: red, garnet, round 4)
    const share = vehicleId === 'motorcycle' ? 0.72 : luma > 0.6 ? 0.26 : 0.3;
    return new THREE.MeshStandardMaterial({
      color: luma > 0.08 ? rc.multiplyScalar(share) : new THREE.Color(0x4f545e),
      roughness: 0.4,
      metalness: 0.75,
      envMapIntensity: 1.0,
    });
  })();
  // M150: woven carbon under a clearcoat for the rebuilt fleet's aero
  // (splitters, diffusers, skirts, wings). The weave is a tiled canvas; the
  // parts get box-projected UVs on first sight (the GLB bolt-ons carry none).
  const carbon = new THREE.MeshPhysicalMaterial({
    color: 0xffffff,
    map: carbonWeaveTexture(),
    roughness: 0.42,
    metalness: 0.1,
    // (a softer coat: at 1.0 the glancing faces, the Formula's wing flaps,
    // mirrored the sky as slate grey again)
    clearcoat: 0.45,
    clearcoatRoughness: 0.22,
    envMapIntensity: 0.8,
  });

  // (a flag: its first colour, the hoist / top band, not the stock paint:
  // a red ring on Argentina, Brazil or Finland read as a leftover, round 2)
  // (a tire set: its rim colour; the stock red ring under Copa's silver or
  // the Golden Boot read as a leftover, round 4)
  const lipSolid = look?.livery || look?.rim !== undefined
    ? new THREE.MeshStandardMaterial({
        color: look.rim ?? look.body ?? look.livery?.flag?.colors[0] ?? color,
        roughness: 0.32,
        metalness: 0.35,
        envMapIntensity: 1.2,
      })
    : null;
  // M153 round 5: the Formula's wings under a striped, hooped or quartered
  // kit are the SHORTS, one solid colour: the stripes crossed the swept
  // planes at an angle and read as roadworks hazard tape (Penarol worst).
  const kitPattern = look?.livery?.kit?.pattern;
  const wingShorts =
    vehicleId === 'f1' && look?.trim !== undefined && kitPattern !== undefined && ['stripes', 'hoops', 'quarters', 'half'].includes(kitPattern)
      ? new THREE.MeshPhysicalMaterial({ color: look.trim, roughness: 0.32, metalness: 0.5, clearcoat: 1.0, clearcoatRoughness: 0.08, envMapIntensity: 1.3 })
      : null;
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh) return;
    m.castShadow = true;
    // M127: classified once per mesh and cached (carPartOf) — a re-dress of
    // the same model (the shop preview) no longer loses the GLB material
    // names that told "Body_1" from the wheel well.
    switch (carPartOf(m)) {
      case 'carbon':
        projectBoxUVs(m);
        m.material = carbon;
        break;
      case 'lens':
        m.material = lens;
        break;
      case 'ledred':
        m.material = ledRed;
        break;
      case 'ledcool':
        m.material = ledCool;
        break;
      case 'amber':
        m.material = amber;
        break;
      case 'charcoal':
        m.material = charcoal;
        break;
      case 'satin':
        m.material = satin;
        break;
      case 'glassclear':
        m.material = glassClear;
        break;
      case 'brake':
        m.material = brake;
        break;
      case 'gold':
        m.material = gold;
        break;
      case 'chrome':
        // M144: a painted wheel covers everything seen between its spokes —
        // the brake disc here, the barrel below. Left as chrome and dark trim
        // they showed as a slate disc through the bone (measured in the garage).
        // M154: a polished rim LIP (the muscle car's Chrome_rimlip) belongs to
        // the wheel's face: an art set paints it and a tire set or a kit's
        // wheel colour takes it (a chrome ring cut across the Dragon Claws)
        if (/^Chrome_disc_/.test(m.name)) m.material = painted ?? discIron;
        else if (/^Chrome_rimlip/.test(m.name)) m.material = painted ?? (look?.rim !== undefined ? rim : chrome);
        else m.material = chrome;
        break;
      case 'trim':
        // M153: a wheel's barrel (behind the spokes) is the rim's colour in
        // SHADOW whenever the look sets a rim: tinted, so a gold Golden Boot
        // rim no longer frames a black hole, and dark, so the spokes still
        // stand off it (a barrel in the full rim colour turned every set
        // into a flat hubcap disc: the second review round, all rides).
        if (/^Trim_barrel_/.test(m.name)) {
          m.material = painted ?? barrel;
          // M154: the lid behind the spokes is the art set's canvas only
          // (the Dragon Claws' bone face); every other look keeps the wheel open
          if (isWheelLid(m.name)) m.visible = painted !== null;
        }
        // M153: the Formula has no bumpers, sills or bed to wear the shorts:
        // its trim (the shark fin, the halo, the airbox) stays dark (a white
        // slab behind the driver on every white-shorts kit, re-review). The
        // monster truck's plain trim is its chassis, axles and links (one
        // joined part): mechanicals, not shorts.
        else m.material = trimIsDark(m) || vehicleId === 'f1' || vehicleId === 'van' ? trimDark : trim;
        break;
      case 'rim':
        m.material = rim;
        break;
      case 'tyre':
        m.material = tyre;
        break;
      case 'glass':
        m.material = glass;
        break;
      case 'head':
        m.material = head;
        break;
      case 'tail':
        m.material = tail;
        break;
      default:
        // M153: the Formula's painted rim lip rings take ONE colour under a
        // livery (the socks, else the body); sampled from the flank painting
        // they turned into two-tone arcs (re-review).
        if (wingShorts && /^(FrontWing|RearWing)/.test(m.name)) {
          m.material = wingShorts;
          break;
        }
        // (the monster truck's beadlock rings too, round 5)
        if (lipSolid && /^(Lipring|Paint_bead_)/i.test(m.name)) {
          m.material = lipSolid;
          break;
        }
        m.material = paint; // body + anything unlabelled (incl. the Helmet — vehicle colour)
        if (flake) ensureFlakeUVs(m); // the flake map needs UVs; the lofted shells ship none
        // M79: a wrap needs draped UVs — the GLBs' loft UVs read as one solid
        // colour (see carLook.projectWrapUVs). M114b: the Formula's stock
        // gradient is sampled through the exact same side-view projection.
        if (paintMap && measure) {
          projectWrapUVs(root, m, measure);
          if (threeView || decal) projectLiveryViews(root, m, measure);
        }
    }
  });
  // M154 r2: under an art set nothing stands in front of a wheel's face (the
  // bike's brake, axle ends and fork legs crossed the Dragon Claws)
  clearArtWheelFronts(root, painted !== null);
}
